// useIsTruncated — the overflow gate for a truncation tooltip.
//
// Tracks whether an element's text is actually CLIPPED by overflow
// (`scrollWidth > clientWidth`) and re-checks on resize, so a label truncated
// only at some widths gains/loses the flag responsively (the gate is a live
// measurement, not a one-time read). Returns a callback ref to attach to the
// measured element (the clipping label) and the current flag.
//
// This is the "only show a tooltip when there is something to recover" rule:
// a label that fits its box needs no tooltip. Measurement is the one impure
// seam (reads layout off a real DOM node); the predicate itself
// (`scrollWidth > clientWidth`) is pure.

import { useCallback, useEffect, useRef, useState } from "react";

export type UseIsTruncated = {
    // Attach to the element whose overflow gates the tooltip (the label span).
    readonly ref: (node: HTMLElement | null) => void;
    // True while that element's content is clipped by overflow.
    readonly truncated: boolean;
};

export const useIsTruncated = (): UseIsTruncated => {
    const [truncated, setTruncated] = useState(false);
    const nodeRef = useRef<HTMLElement | null>(null);

    const measure = useCallback(() => {
        const node = nodeRef.current;
        if (node === null) return;
        setTruncated(node.scrollWidth > node.clientWidth);
    }, []);

    // Callback ref: remember the node and take an initial measurement the
    // moment it mounts (before the ResizeObserver's first callback lands).
    const ref = useCallback(
        (node: HTMLElement | null) => {
            nodeRef.current = node;
            measure();
        },
        [measure],
    );

    // Re-measure whenever the element resizes (rail drag, window resize, font
    // load). ResizeObserver fires once on observe, so the initial size is
    // covered here too. Guarded for non-DOM/test environments.
    useEffect(() => {
        const node = nodeRef.current;
        if (node === null || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(() => measure());
        observer.observe(node);
        return () => observer.disconnect();
    }, [measure]);

    return { ref, truncated };
};
